import { useCallback } from 'react'

import { toast } from 'sonner'

import { shouldForceMicrosoftConsentPrompt } from '@/hooks/mcp/microsoftPermissions'
import { useMicrosoftOrgServer } from '@/hooks/mcp/useOrganizationMCPServers'
import { useStoreMCPOAuthTokens } from '@/hooks/mcp/useStoreMCPOAuthTokens'
import { useCanEnableSharepoint } from '@/hooks/sharepoint/useCanEnableSharepoint'
import { useSharepointSetupStatus } from '@/hooks/sharepoint/useSharepointSetupStatus'

import type { UUID } from '@/types/Id'

export type SharepointDropdownSublabelTone = 'default' | 'warning'

interface UseSharepointDropdownOptionArgs {
  orgId: UUID | ''
  onAuthenticated: () => void
}

const getSharepointSublabel = ({
  isLoadingOrgServers,
  hasMicrosoftOrgServer,
  isAuthenticating,
  isAuthenticated,
  needsReauth,
}: {
  isLoadingOrgServers: boolean
  hasMicrosoftOrgServer: boolean
  isAuthenticating: boolean
  isAuthenticated: boolean
  needsReauth: boolean
}) => {
  if (isLoadingOrgServers) {
    return undefined
  }

  if (!hasMicrosoftOrgServer) {
    return 'Connect SharePoint in Settings'
  }

  if (isAuthenticating) {
    return 'Authenticating...'
  }

  if (!isAuthenticated) {
    return needsReauth
      ? 'Permissions updated — click to reconnect'
      : 'Click to authenticate'
  }

  return undefined
}

const getSharepointSublabelTone = ({
  isLoadingOrgServers,
  hasMicrosoftOrgServer,
  isAuthenticated,
}: {
  isLoadingOrgServers: boolean
  hasMicrosoftOrgServer: boolean
  isAuthenticated: boolean
}): SharepointDropdownSublabelTone => {
  if (!hasMicrosoftOrgServer || isLoadingOrgServers) {
    return 'default'
  }

  return isAuthenticated ? 'default' : 'warning'
}

export const useSharepointDropdownOption = ({
  orgId,
  onAuthenticated,
}: UseSharepointDropdownOptionArgs) => {
  const canEnableSharepoint = useCanEnableSharepoint()

  const { data: microsoftOrgServer, isLoading: isLoadingOrgServers } =
    useMicrosoftOrgServer({ orgId })

  const hasUserToken = microsoftOrgServer?.user_has_token === true
  const needsReauth = microsoftOrgServer?.needs_reauth === true
  const usesCustomCredentials =
    !!microsoftOrgServer?.oauth_client_id &&
    microsoftOrgServer?.has_client_secret === true
  const isAuthenticated = hasUserToken && !needsReauth

  const { status, setStatus } = useSharepointSetupStatus({ orgId })
  const isAuthenticating = status === 'connecting' || status === 'waiting'

  const handleStoreStart = useCallback(() => {
    setStatus('waiting')
  }, [setStatus])

  const handleStoreError = useCallback(() => {
    setStatus('idle')
  }, [setStatus])

  const handleTokensStored = useCallback(() => {
    onAuthenticated()
  }, [onAuthenticated])

  const { startOAuthFlow } = useStoreMCPOAuthTokens({
    orgId,
    microsoftOrgServer,
    onTokensStored: handleTokensStored,
    onStoreStart: handleStoreStart,
    onStoreError: handleStoreError,
  })

  const handleSelectSharepoint = useCallback(() => {
    if (!microsoftOrgServer || isLoadingOrgServers || isAuthenticating) {
      return
    }

    if (isAuthenticated) {
      onAuthenticated()
      return
    }

    setStatus('connecting')

    void (async () => {
      try {
        await startOAuthFlow({
          forceConsentPrompt: shouldForceMicrosoftConsentPrompt({
            usesCustomCredentials,
            hasUserToken,
            needsReauth,
          }),
        })
      } catch (error: unknown) {
        if (
          error instanceof Error &&
          error.message === 'Authorization window was closed'
        ) {
          setStatus('idle')
          return
        }

        console.error('MCP OAuth error:', error)
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to authorize. Please try again.'
        toast.error(message)
        setStatus('idle')
      }
    })()
  }, [
    hasUserToken,
    isAuthenticated,
    isAuthenticating,
    isLoadingOrgServers,
    microsoftOrgServer,
    needsReauth,
    onAuthenticated,
    setStatus,
    startOAuthFlow,
    usesCustomCredentials,
  ])

  const sharepointDisabled =
    isLoadingOrgServers || !microsoftOrgServer || isAuthenticating
  const sharepointRequiresOrgSetup =
    !isLoadingOrgServers &&
    (!microsoftOrgServer || !microsoftOrgServer.is_active)

  const sharepointSublabel = getSharepointSublabel({
    isLoadingOrgServers,
    hasMicrosoftOrgServer: !!microsoftOrgServer,
    isAuthenticating,
    isAuthenticated,
    needsReauth,
  })

  const sharepointSublabelTone = getSharepointSublabelTone({
    isLoadingOrgServers,
    hasMicrosoftOrgServer: !!microsoftOrgServer,
    isAuthenticated,
  })

  return {
    showSharepoint: canEnableSharepoint,
    sharepointDisabled,
    sharepointRequiresOrgSetup,
    sharepointSublabel,
    sharepointSublabelTone,
    handleSelectSharepoint,
  }
}
